import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, ChevronRight, CreditCard, Download, FileCheck, Heart, Mail, MapPin, Phone, Printer, QrCode, Send, ShieldCheck, ShoppingBag, Smile, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useSite } from "../lib/store";
import { Daisy, FlowerBlob, Wave } from "../components/Daisy";
import { useSeo } from "../components/Seo";

const parse = (text) => {
  const lead = [];
  const sections = [];
  (text || "").split("\n").filter((l) => l.trim()).forEach((l) => {
    if (l.startsWith("## ")) sections.push({ title: l.slice(3).replace(/^\d+\.\s*/, ""), body: [] });
    else if (sections.length) sections.at(-1).body.push(l);
    else lead.push(l);
  });
  return { lead, sections };
};

export const RichText = ({ text }) => {
  const { lead, sections } = parse(text);
  return (
    <div className="space-y-5">
      {lead.map((p, i) => <p key={i} className={i === 0 ? "font-serif text-xl italic leading-relaxed text-[#7A8460] sm:text-2xl" : "leading-relaxed text-[#2D3426]/85"}>{p}</p>)}
      {sections.map((s, i) => (
        <motion.section key={i} id={`sekce-${i + 1}`} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
          className="group relative scroll-mt-28 rounded-[1.75rem] border bg-white p-6 transition-shadow hover:shadow-[0_18px_40px_-24px_rgba(45,52,38,.45)] sm:p-8" data-testid={`content-section-${i + 1}`}>
          <div className="flex items-start gap-4">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#E9EFE0] font-serif text-lg font-black italic text-[#7A8460] transition-colors group-hover:bg-[#A3AC86] group-hover:text-white">{i + 1}</span>
            <div className="min-w-0">
              <h2 className="h-section text-xl sm:text-2xl">{s.title}</h2>
              {s.body.map((p, k) => <p key={k} className="mt-3 leading-relaxed text-[#2D3426]/85">{p}</p>)}
            </div>
          </div>
        </motion.section>
      ))}
    </div>
  );
};

const PageHero = ({ title, eyebrow, lead }) => (
  <section className="relative overflow-hidden bg-[#E9EFE0]">
    <FlowerBlob className="animate-spin-slow absolute -right-24 -top-24 h-72 w-72 opacity-40 sm:h-96 sm:w-96" />
    <div className="animate-float absolute right-8 top-10 hidden sm:block"><Daisy size={70} /></div>
    <div className="animate-float absolute bottom-16 right-1/3 hidden md:block" style={{ animationDelay: "1.5s" }}><Daisy size={34} /></div>
    <div className="relative mx-auto max-w-6xl px-5 pb-6 pt-10 sm:px-8 sm:pt-16">
      <nav className="flex items-center gap-1 text-xs text-[#6E7565]" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-[#7A8460]">Domů</Link><ChevronRight className="h-3 w-3" /><span className="text-[#2D3426]">{title}</span>
      </nav>
      {eyebrow && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 font-script text-2xl text-[#D96B43]">{eyebrow}</motion.p>}
      <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", damping: 16 }} className="h-display mt-1 max-w-3xl text-4xl leading-[1.02] sm:text-6xl" data-testid="page-title">{title}</motion.h1>
      {lead && <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mt-5 max-w-xl text-[#2D3426]/80 sm:text-lg">{lead}</motion.p>}
    </div>
    <Wave fill="#F7F3EA" />
  </section>
);

const Steps = ({ title, items }) => (
  <section className="mt-14">
    <h2 className="h-section mb-6 text-2xl sm:text-3xl">{title}</h2>
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {items.map(([I, t, d], i) => (
        <motion.div key={t} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
          className="relative rounded-[1.75rem] bg-white p-6 text-center shadow-[0_10px_30px_-22px_rgba(45,52,38,.5)]">
          <span className="absolute left-4 top-4 font-serif text-4xl font-black italic text-[#E9EFE0]">{i + 1}</span>
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#A3AC86] text-white shadow-md transition-transform duration-500 hover:rotate-12"><I className="h-7 w-7" strokeWidth={1.5} /></span>
          <p className="mt-4 font-semibold">{t}</p>
          <p className="mt-1 text-sm text-[#6E7565]">{d}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

const Values = () => (
  <section className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
    {[[Smile, "Učení hrou", "Děti se učí nejvíc, když je to baví."], [Heart, "Pro děti 3–7 let", "Obsah přizpůsobený předškolnímu věku."], [Download, "Okamžitě k dispozici", "Stahujete hned po zaplacení."], [Printer, "Tisk doma i ve školce", "Vytisknete tolikrát, kolikrát potřebujete."]].map(([I, t, d], i) => (
      <motion.div key={t} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
        className={`rounded-[1.75rem] p-6 ${i % 2 ? "bg-[#A3AC86] text-white" : "bg-white"}`}>
        <I className="h-8 w-8" strokeWidth={1.4} />
        <p className="mt-4 font-serif text-xl font-extrabold italic">{t}</p>
        <p className={`mt-1 text-sm ${i % 2 ? "text-white/85" : "text-[#6E7565]"}`}>{d}</p>
      </motion.div>
    ))}
  </section>
);

const CtaBand = () => (
  <motion.section initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    className="grain relative mt-16 flex flex-col items-center gap-5 overflow-hidden rounded-[2rem] bg-[#2D3426] p-8 text-center text-white sm:flex-row sm:p-10 sm:text-left">
    <span className="grid h-20 w-20 shrink-0 place-items-center rounded-full bg-[#A3AC86]"><Daisy size={58} className="animate-spin-slow" /></span>
    <div className="flex-1">
      <p className="font-serif text-2xl font-extrabold italic sm:text-3xl">Máte otázku? Rádi pomůžeme.</p>
      <p className="mt-1 text-sm text-white/70">Napište nám, nebo se rovnou podívejte, co nového v e-shopu rozkvetlo.</p>
    </div>
    <div className="flex flex-wrap justify-center gap-3">
      <Link to="/kontakt" className="btn-yellow" data-testid="cta-contact-btn">Kontakt</Link>
      <Link to="/eshop" className="btn-ghost !border-white/40 !text-white hover:!bg-white/10" data-testid="cta-shop-btn">E-shop <ArrowRight className="h-4 w-4" /></Link>
    </div>
  </motion.section>
);

const Toc = ({ sections }) => (
  <aside className="min-w-0 lg:sticky lg:top-28 lg:h-fit">
    <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-[#6E7565]">Obsah</p>
    <nav className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 lg:mx-0 lg:flex-col lg:px-0" data-testid="content-toc">
      {sections.map((s, i) => (
        <a key={i} href={`#sekce-${i + 1}`} className="flex shrink-0 items-center gap-2 rounded-full bg-white px-4 py-2 text-sm transition-colors hover:bg-[#E9EFE0] lg:rounded-2xl">
          <span className="font-serif font-black italic text-[#A3AC86]">{i + 1}</span> {s.title}
        </a>
      ))}
    </nav>
    <div className="mt-6 hidden rounded-[1.75rem] bg-[#E9EFE0] p-5 text-sm lg:block">
      <ShieldCheck className="h-6 w-6 text-[#7A8460]" />
      <p className="mt-2 font-semibold">Nakupujete bezpečně</p>
      <p className="mt-1 text-[#6E7565]">Platby zpracovává Stripe, vaše údaje chráníme dle GDPR.</p>
    </div>
  </aside>
);

const PAGES = {
  about: { title: "O nás", eyebrow: "Kdo za kopretinou stojí", lead: "Malé kroky, velké objevy – hravé materiály pro děti, rodiče i pedagogy.", key: ["texts", "about_page"] },
  terms: { title: "Obchodní podmínky", eyebrow: "Férově a přehledně", lead: "Vše, co potřebujete vědět o nákupu digitálních materiálů.", key: ["legal", "terms"], toc: true },
  gdpr: { title: "Ochrana osobních údajů", eyebrow: "Vaše data v bezpečí", lead: "Jak pečujeme o vaše osobní údaje a cookies.", key: ["legal", "gdpr"], toc: true },
  payment: { title: "Doprava a platba", eyebrow: "Bez čekání na kurýra", lead: "Digitální produkty doručujeme rovnou do vašeho účtu – doprava je zdarma.", key: ["texts", "payment_page"] },
  complaints: { title: "Reklamace a vrácení", eyebrow: "Jsme tu pro vás", lead: "Když něco nefunguje, vyřešíme to rychle a s úsměvem.", key: ["texts", "complaints_page"] },
};

const BUY_STEPS = [[ShoppingBag, "Vyberte", "Vložte materiály do košíku."], [CreditCard, "Zaplaťte", "Kartou online nebo QR převodem."], [Download, "Stáhněte", "Soubory najdete v Můj účet → Soubory."], [BookOpen, "Hrajte si", "Vytiskněte a můžete začít."]];

const PaymentMethods = () => (
  <section className="mt-14 grid gap-4 md:grid-cols-3">
    {[[CreditCard, "Platební karta", "Platba kartou přes zabezpečenou bránu Stripe. Soubory zpřístupníme ihned."], [QrCode, "QR platba / převod", "Naskenujte QR kód v bankovní aplikaci. Soubory zpřístupníme po připsání platby."], [FileCheck, "Faktura e-mailem", "Po zaplacení automaticky obdržíte fakturu, najdete ji i ve svém účtu."]].map(([I, t, d], i) => (
      <motion.div key={t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="rounded-[1.75rem] border bg-white p-6">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#F2C230]/30 text-[#7A8460]"><I className="h-6 w-6" /></span>
        <p className="mt-4 font-serif text-xl font-extrabold italic text-[#2D3426]">{t}</p>
        <p className="mt-2 text-sm text-[#6E7565]">{d}</p>
      </motion.div>
    ))}
  </section>
);

export default function ContentPage({ page }) {
  const { site } = useSite();
  const cfg = PAGES[page];
  const s = site?.settings || {};
  const text = s[cfg.key[0]]?.[cfg.key[1]];
  useSeo({ title: cfg.title, description: cfg.lead });
  const { sections } = parse(text);
  return (
    <div data-testid={`content-page-${page}`} className="overflow-x-hidden">
      <PageHero title={cfg.title} eyebrow={cfg.eyebrow} lead={cfg.lead} />
      <div className="mx-auto max-w-6xl px-5 pt-6 sm:px-8">
        {cfg.toc && sections.length > 1 ? (
          <div className="grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)]"><Toc sections={sections} /><div className="min-w-0"><RichText text={text} /></div></div>
        ) : (
          <div className="mx-auto max-w-3xl"><RichText text={text} /></div>
        )}
        {page === "about" && <><Values /><Steps title="Jak to u nás funguje" items={BUY_STEPS} /></>}
        {page === "payment" && <><PaymentMethods /><Steps title="Od košíku ke hraní" items={BUY_STEPS} /></>}
        {page === "complaints" && <Steps title="Jak postupovat" items={[[Mail, "Napište nám", "E-mailem nebo přes kontaktní formulář."], [FileCheck, "Číslo objednávky", "Uveďte ho, ať vás rychle najdeme."], [Sparkles, "Prověříme to", "Podíváme se, co se s souborem stalo."], [Download, "Nový soubor", "Obratem pošleme funkční verzi."]]} />}
        <CtaBand />
      </div>
    </div>
  );
}

export function Contact() {
  const { site } = useSite();
  const c = site?.settings?.company || {};
  useSeo({ title: "Kontakt" });
  const [f, setF] = useState({ name: "", email: "", message: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", f);
      toast.success("Zpráva odeslána, brzy se ozveme!");
      setF({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div data-testid="contact-page">
      <PageHero title="Kontakt" eyebrow="Napište nám, rádi si čteme" lead={site?.settings?.texts?.contact_page} />
      <div className="mx-auto grid max-w-6xl gap-6 px-5 pt-6 sm:px-8 md:grid-cols-[1fr_1.3fr]">
        <div className="space-y-3">
          {[[Mail, "E-mail", c.email, `mailto:${c.email}`], [Phone, "Telefon", c.phone, `tel:${c.phone}`], [MapPin, "Adresa", `${c.street || ""}, ${c.zip || ""} ${c.city || ""}`]].map(([I, l, v, href], i) => (
            <motion.a key={i} href={href} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }} className="group flex items-center gap-4 rounded-[1.75rem] border bg-white p-5 transition-transform hover:-translate-y-1">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-[#A3AC86] text-white transition-transform group-hover:rotate-12"><I className="h-5 w-5" /></span>
              <span><span className="block text-xs uppercase tracking-widest text-[#6E7565]">{l}</span><span className="text-sm font-medium">{v}</span></span>
            </motion.a>
          ))}
          <div className="rounded-[1.75rem] bg-[#E9EFE0] p-5 text-sm text-[#6E7565]"><p className="font-semibold text-[#2D3426]">{c.owner}</p><p>IČO: {c.ico}{c.dic ? ` · DIČ: ${c.dic}` : ""}</p><p>{c.register_note}</p></div>
        </div>
        <form onSubmit={submit} className="space-y-4 rounded-[1.75rem] border bg-white p-6 sm:p-8">
          <p className="h-section text-2xl">Napište nám</p>
          <input className="input-soft" required placeholder="Jméno" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name" />
          <input className="input-soft" required type="email" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email" />
          <textarea className="input-soft min-h-[140px]" required placeholder="Vaše zpráva" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message" />
          <button className="btn-sage w-full" data-testid="contact-submit-btn"><Send className="h-4 w-4" /> Odeslat zprávu</button>
        </form>
      </div>
    </div>
  );
}
